import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  createUser,
  grantUserPermission,
  listAdminUsers,
  listProjects,
  revokeUserPermission,
  setUserActive,
} from '../api/client';
import BrandLogo from '../components/BrandLogo';
import UserMenu from '../components/UserMenu';
import Spinner from '../components/ui/Spinner';
import {
  fromApiPermission,
  permissionDescription,
  permissionLabel,
  toApiPermission,
  type AssignablePermission,
} from '../lib/permissions';
import type { AdminUser } from '../types/auth';
import type { ProjectSummary } from '../types/project';

function PermissionToggle({
  value,
  onChange,
  disabled,
  size = 'sm',
}: {
  value: AssignablePermission;
  onChange: (level: AssignablePermission) => void;
  disabled?: boolean;
  size?: 'sm' | 'xs';
}) {
  const btnClass =
    size === 'xs'
      ? 'px-2.5 py-1 text-xs'
      : 'px-3 py-1.5 text-sm';

  return (
    <div
      className="inline-flex rounded-lg border border-border bg-surface p-0.5"
      role="group"
      aria-label="Permission level"
    >
      {(['use', 'edit'] as const).map((level) => (
        <button
          key={level}
          type="button"
          disabled={disabled}
          onClick={() => onChange(level)}
          title={permissionDescription(level)}
          className={`rounded-md font-medium transition-colors ${btnClass} ${
            value === level
              ? level === 'edit'
                ? 'bg-kromeka-red text-white shadow-sm'
                : 'bg-surface-overlay text-text'
              : 'text-text-muted hover:text-text'
          }`}
        >
          {permissionLabel(level)}
        </button>
      ))}
    </div>
  );
}

export default function AdminUsersPage() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const [grantUserId, setGrantUserId] = useState<string | null>(null);
  const [grantProjectId, setGrantProjectId] = useState('');
  const [grantPermission, setGrantPermission] = useState<AssignablePermission>('use');
  const [granting, setGranting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [userList, projectList] = await Promise.all([
        listAdminUsers(),
        listProjects(true),
      ]);
      setUsers(userList);
      setProjects(projectList);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    try {
      await createUser({
        email: newEmail.trim(),
        password: newPassword,
        display_name: newName.trim() || newEmail.trim(),
        role: 'user',
      });
      setNewEmail('');
      setNewPassword('');
      setNewName('');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (user: AdminUser) => {
    try {
      await setUserActive(user.id, !user.active);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    }
  };

  const handleGrant = async (userId: string) => {
    if (!grantProjectId) return;
    setGranting(true);
    setError(null);
    try {
      await grantUserPermission(userId, {
        project_id: grantProjectId,
        permission: toApiPermission(grantPermission),
      });
      setGrantUserId(null);
      setGrantProjectId('');
      setGrantPermission('use');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to grant access');
    } finally {
      setGranting(false);
    }
  };

  const handleChangePermission = async (
    userId: string,
    projectId: string,
    level: AssignablePermission,
  ) => {
    setError(null);
    try {
      await grantUserPermission(userId, {
        project_id: projectId,
        permission: toApiPermission(level),
      });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update permission');
    }
  };

  const handleRevoke = async (userId: string, projectId: string) => {
    try {
      await revokeUserPermission(userId, projectId);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke access');
    }
  };

  const unassignedProjects = (user: AdminUser) => {
    const assigned = new Set(user.project_permissions.map((p) => p.project_id));
    return projects.filter((p) => !assigned.has(p.id));
  };

  return (
    <div className="min-h-full overflow-auto bg-surface">
      <header className="border-b border-border bg-surface-raised/90 backdrop-blur-sm">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <BrandLogo variant="full" linkToHome />
            <span className="text-sm text-text-muted">User management</span>
          </div>
          <UserMenu />
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text">Users</h1>
            <p className="mt-1 text-sm text-text-muted">
              Create customer accounts and assign projects with{' '}
              <strong className="font-medium text-text">Use</strong> or{' '}
              <strong className="font-medium text-text">Edit</strong> access.
            </p>
          </div>
          <Link to="/" className="btn-secondary text-sm">
            Back to library
          </Link>
        </div>

        {error && (
          <div className="alert-danger mb-6 px-4 py-3 text-sm">
            {error}
          </div>
        )}

        <section className="mb-6 rounded-xl border border-border bg-surface-raised/60 px-5 py-4 text-sm text-text-muted">
          <p>
            <strong className="text-text">Use</strong> — customer can open the project and run
            simulations; the layout is read-only.
          </p>
          <p className="mt-1">
            <strong className="text-text">Edit</strong> — customer can change the layout, nodes,
            routes, and settings.
          </p>
        </section>

        <section className="mb-10 rounded-xl border border-border bg-surface-raised p-6">
          <h2 className="text-lg font-semibold text-text">Create user</h2>
          <form
            onSubmit={handleCreateUser}
            className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
          >
            <input
              type="text"
              placeholder="Display name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="input-field"
            />
            <input
              type="email"
              placeholder="Email"
              required
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="input-field"
            />
            <input
              type="password"
              placeholder="Password"
              required
              minLength={8}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="input-field"
            />
            <button
              type="submit"
              disabled={creating}
              className="btn-primary sm:col-span-2 lg:col-span-1"
            >
              {creating ? 'Creating…' : 'Create user'}
            </button>
          </form>
        </section>

        {loading ? (
          <div className="flex justify-center py-16">
            <Spinner label="Loading users…" />
          </div>
        ) : (
          <div className="space-y-4">
            {users.map((user) => {
              const available = unassignedProjects(user);

              return (
                <article
                  key={user.id}
                  className="rounded-xl border border-border bg-surface-raised p-5"
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <h3 className="font-semibold text-text">
                        {user.display_name}
                        {!user.active && (
                          <span className="ml-2 badge-danger-on-dark px-2 py-0.5 text-xs">
                            inactive
                          </span>
                        )}
                      </h3>
                      <p className="text-sm text-text-muted">{user.email}</p>
                      <p className="mt-1 text-xs text-text-subtle">
                        {user.role} · joined{' '}
                        {new Date(user.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    {user.role !== 'admin' && (
                      <button
                        type="button"
                        onClick={() => void handleToggleActive(user)}
                        className="btn-ghost text-xs"
                      >
                        {user.active ? 'Deactivate' : 'Activate'}
                      </button>
                    )}
                  </div>

                  <div className="mt-4 border-t border-border-subtle pt-4">
                    <h4 className="panel-section-title mb-2">Project access</h4>

                    {user.role === 'admin' ? (
                      <p className="text-sm text-text-subtle">
                        Admins have access to all projects automatically.
                      </p>
                    ) : (
                      <>
                        {user.project_permissions.length === 0 ? (
                          <p className="text-sm text-text-subtle">No projects assigned yet.</p>
                        ) : (
                          <ul className="space-y-2">
                            {user.project_permissions.map((perm) => {
                              const level = fromApiPermission(perm.permission);
                              const assignable: AssignablePermission =
                                level === 'edit' ? 'edit' : 'use';

                              return (
                                <li
                                  key={perm.project_id}
                                  className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-surface px-3 py-2.5"
                                >
                                  <span className="min-w-0 flex-1 truncate font-medium text-text">
                                    {perm.project_name}
                                  </span>
                                  <div className="flex flex-wrap items-center gap-2">
                                    {level === 'view' ? (
                                      <span className="badge-warning-on-dark border border-warning/40 px-2 py-0.5 text-xs">
                                        View only
                                      </span>
                                    ) : (
                                      <PermissionToggle
                                        value={assignable}
                                        size="xs"
                                        onChange={(next) =>
                                          void handleChangePermission(
                                            user.id,
                                            perm.project_id,
                                            next,
                                          )
                                        }
                                      />
                                    )}
                                    <button
                                      type="button"
                                      onClick={() =>
                                        void handleRevoke(user.id, perm.project_id)
                                      }
                                      className="btn-danger-ghost px-2 py-1 text-xs"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        )}

                        <div className="mt-4 rounded-lg border border-dashed border-border bg-surface/50 p-4">
                          {grantUserId === user.id ? (
                            <div className="space-y-3">
                              <p className="text-sm font-medium text-text">Add project access</p>
                              <div className="flex flex-wrap items-end gap-4">
                                <label className="min-w-[14rem] flex-1 text-xs text-text-muted">
                                  Project
                                  <select
                                    value={grantProjectId}
                                    onChange={(e) => setGrantProjectId(e.target.value)}
                                    className="mt-1 block w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text"
                                  >
                                    <option value="">Select a project…</option>
                                    {available.map((p) => (
                                      <option key={p.id} value={p.id}>
                                        {p.name}
                                        {p.archived ? ' (archived)' : ''}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                                <div>
                                  <span className="text-xs text-text-muted">Permission</span>
                                  <div className="mt-1">
                                    <PermissionToggle
                                      value={grantPermission}
                                      onChange={setGrantPermission}
                                    />
                                  </div>
                                  <p className="mt-1 text-xs text-text-subtle">
                                    {permissionDescription(grantPermission)}
                                  </p>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => void handleGrant(user.id)}
                                  disabled={!grantProjectId || granting}
                                  className="btn-primary px-4 py-2 text-sm"
                                >
                                  {granting ? 'Adding…' : 'Add project'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setGrantUserId(null);
                                    setGrantProjectId('');
                                    setGrantPermission('use');
                                  }}
                                  className="btn-ghost px-4 py-2 text-sm"
                                >
                                  Cancel
                                </button>
                              </div>
                              {available.length === 0 && (
                                <p className="text-xs text-text-subtle">
                                  All projects are already assigned to this user.
                                </p>
                              )}
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                setGrantUserId(user.id);
                                setGrantProjectId('');
                                setGrantPermission('use');
                              }}
                              disabled={available.length === 0 && projects.length > 0}
                              className="btn-secondary text-sm"
                            >
                              + Add project
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
